// ************************************************************************************************************
// THIS FILE HAS BEEN DESIGNATED INTERCHANGABLE BETWEEN BOTH PATIENT/PROVIDER APPS.
// VERIFY CHANGES TO THIS FILE MAKE SENSE IN BOTH APPS BY SYNCING CHANGES TO BOTH LOCATIONS DURING DEVELOPMENT.
// ************************************************************************************************************

import React, { Component, createContext } from 'react';
import {
  ApplicationInsights,
  DistributedTracingModes,
  IEventTelemetry,
  IPageViewTelemetry,
  ITraceTelemetry,
  IExceptionTelemetry,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { IConfiguration, ICustomProperties, ITelemetryItem, Tags } from '@microsoft/applicationinsights-core-js';
import { IConfig } from '@microsoft/applicationinsights-common';

const reactPlugin = new ReactPlugin();

interface IInitProps {
  key: string;
  config?: Partial<IConfiguration & IConfig>;
  shouldDelayTelemetry?: (envelope: ITelemetryItem) => boolean;
  callback?: () => void;
}

export class AppInsights {
  private applicationInsights: ApplicationInsights | undefined;
  public enabled = false;
  private delayedTelemetryCleanupTimeoutID: ReturnType<typeof setTimeout> | undefined;
  private delayedTelemetry: ITelemetryItem[] = [];
  private delayedTelemetrySending = false;
  public customTelemetryData: ICustomProperties = {};
  public customTelemetryTags: Tags & Tags[] = [];
  private throttledExceptions: { text: string; max: number; count: number }[] = [];
  // ignore tracking telemetry for amplitude and chime event ingestion targets by default
  private ignoredTargets = ['api.amplitude.com', 'ingest.chime.aws'];

  // Specify text in an exception that would cause it to be throttled.
  // max indicates the maximum instance that will be reported to app insights.
  // setting max to 0 will prevent this exception telemetry from being sent altogether
  public addThrottledException = (text: string, max: number = 1) => {
    this.throttledExceptions.push({
      text,
      max,
      count: 0,
    });
  };

  public resetThrottledExceptionCounts() {
    for (let teIndex = 0; teIndex < this.throttledExceptions.length; teIndex++) {
      this.throttledExceptions[teIndex].count = 0;
    }
  }

  public addIgnoredTarget(target: string) {
    this.ignoredTargets.push(target);
  }

  private _shouldDelayTelemetry: undefined | ((envelope: ITelemetryItem) => boolean) = undefined;
  public shouldDelayTelemetry = (envelope: ITelemetryItem): boolean => {
    return (this._shouldDelayTelemetry && this._shouldDelayTelemetry(envelope)) || false;
  };

  private sendDelayedTelemetry = () => {
    // since calls to appInsights.core.track invoke mainTelemetryInitializer again,
    // delayedTelemetrySending is set here to prevent an infinite loop
    this.delayedTelemetrySending = true;

    this.delayedTelemetry.forEach((envelope: ITelemetryItem) => {
      this.applicationInsights?.core.track(envelope);
    });

    this.delayedTelemetry = [];
    this.delayedTelemetrySending = false;
  };

  private mainTelemetryInitializer = (envelope: ITelemetryItem) => {
    // discard telemetry when appInsights is disabled
    if (!this.enabled) {
      return false;
    }

    // discard RemoteDependency telemetry for any ignored target hosts
    if (envelope.name.includes('RemoteDependency')) {
      if (this.ignoredTargets.filter((t) => envelope.baseData?.target?.includes(t)).length > 0) {
        return false;
      }
    }

    // if telemetry should be delayed until a future condition is met, ex. when a
    // custom telemetry tag/data is set, save each telemetry item that comes through
    // and then tell app insights to discard it
    if (!this.delayedTelemetrySending) {
      if (this.shouldDelayTelemetry(envelope)) {
        this.delayedTelemetry.push(envelope);

        if (this.delayedTelemetryCleanupTimeoutID) {
          clearTimeout(this.delayedTelemetryCleanupTimeoutID);
        }
        this.delayedTelemetryCleanupTimeoutID = setTimeout(() => {
          this.sendDelayedTelemetry();
        }, 5000);

        return false; // tells app insights to discard this telemetry
      } else if (this.delayedTelemetry.length) {
        // At this point we have delayed telemetry and should not be delaying any
        // further, resubmit all previously saved telemetry items

        // add this most recent telemetry to the end of the delayedTelemetry cache
        this.delayedTelemetry.push(envelope);

        this.sendDelayedTelemetry();

        return false; // tells app insights to discard this telemetry item
      }
    }

    // if this telemetry matches a throttled exception, throttle it
    if (envelope.name.includes('Exception')) {
      for (let teIndex = 0; teIndex < this.throttledExceptions.length; teIndex++) {
        const throttledException = this.throttledExceptions[teIndex];
        for (let eIndex = 0; eIndex < envelope.baseData?.exceptions.length; eIndex++) {
          const exception = envelope.baseData?.exceptions[eIndex];
          if (exception.message.includes(throttledException.text)) {
            throttledException.count++;
            if (throttledException.count > throttledException.max) {
              return false; // tells app insights to discard this telemetry item
            }
          }
        }
      }
    }

    // add custom tags to telemetry, if any exist
    if (envelope.tags && Object.keys(this.customTelemetryTags).length) {
      envelope.tags = { ...envelope.tags, ...this.customTelemetryTags };
    }

    // add custom data to telemetry, if any exist
    if (Object.keys(this.customTelemetryData).length) {
      envelope.data = { ...envelope.data, ...this.customTelemetryData };
    }
  };

  public init({ key, config, shouldDelayTelemetry, callback }: IInitProps) {
    if (!this.applicationInsights) {
      this.applicationInsights = new ApplicationInsights({
        config: {
          connectionString: `InstrumentationKey=${key};IngestionEndpoint=https://eastus2-0.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus2.livediagnostics.monitor.azure.com/`,
          distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
          enableUnhandledPromiseRejectionTracking: true,
          extensions: [reactPlugin],
          ...config,
        },
      });

      this.applicationInsights.loadAppInsights();
      this.applicationInsights.addTelemetryInitializer(this.mainTelemetryInitializer);
      if (callback) {
        callback();
      }
    }
    this.delayedTelemetry = [];
    this.delayedTelemetrySending = false;
    this._shouldDelayTelemetry = shouldDelayTelemetry;
    this.enabled = true;
  }

  public trackEvent = (event: IEventTelemetry, customProperties?: ICustomProperties) => {
    this.applicationInsights?.trackEvent(event, customProperties);
  };

  public trackPageView = (pageView: IPageViewTelemetry) => {
    this.applicationInsights?.trackPageView(pageView);
  };

  public trackTrace = (trace: ITraceTelemetry) => {
    this.applicationInsights?.trackTrace(trace);
  };

  public trackException = (exception: IExceptionTelemetry) => {
    this.applicationInsights?.trackException(exception);
  };

  public flush = () => {
    this.applicationInsights?.flush();
  };

  public addTelemetryInitializer = (telemetryInitializer: (item: ITelemetryItem) => boolean | void) => {
    this.applicationInsights?.addTelemetryInitializer(telemetryInitializer);
  };

  public getTraceCtx = () => {
    return this.applicationInsights?.getTraceCtx();
  };
}

const appInsights: AppInsights = new AppInsights();

// Setup Telemetry Provider
const AppInsightsContext: React.Context<ReactPlugin> = createContext(reactPlugin);
class TelemetryProvider extends Component<WithChildren> {
  render() {
    return <AppInsightsContext.Provider value={reactPlugin}>{this.props.children}</AppInsightsContext.Provider>;
  }
}

export { appInsights, SeverityLevel };
export default withAITracking(reactPlugin, TelemetryProvider, 'AITracker', 'fe_u_fill--parent');
